import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { API_BASE_URL, BaseUrls } from '@app/app.constants';
import { Feature } from '@app/core/interfaces/c5/feature';
import { NewTrunk } from '@app/core/interfaces/portal/new-trunk';
import { TeamsTrunkUpdate } from '@app/core/interfaces/portal/teams-trunk-update';
import { TrunkAssignmentResult } from '@app/core/interfaces/portal/trunk-assignment-result';
import { TrunkUpdate } from '@app/core/interfaces/portal/trunk-update';
import { TeamsDomainSuggestion } from '@app/core/interfaces/teams-domain-suggestion';
import { TeamsSBC } from '@app/core/interfaces/teams-sbc';
import { TrunkCommon } from '@app/core/interfaces/trunk-common';
import { TrunkCredentials } from '@app/core/interfaces/trunk-credentials';
import { TrunkProfile } from '@app/core/interfaces/trunk-profile';
import { UserLevel } from '@app/core/models/user-level';
import { Utils } from '@app/shared/services/utils.service';
import { Identifier } from '@app/utils/common';
import { wrapError } from '@app/utils/rxjs';
import { Features } from '@business/feature/interfaces/features';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

interface ListOptions {
  /**
   * Load only teams trunks, non-teams trunks or all trunks
   *  - if true: only teams trunks
   *  - if false: only non-teams trunks
   *  - if undefined: all trunks
   */
  teams?: boolean;

  /**
   * Load profiles as well
   */
  withProfile?: boolean;
}

interface ListProfilesOptions {
  /**
   * If the specific profile settings should be included in the response.
   */
  includeJson: boolean;

  /**
   *  - if true: only teams trunk profiles
   *  - if false: only non-teams trunk profiles
   *  - if undefined: all trunk profiles
   */
  teams?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class TrunkRepository {
  constructor(
    private http: HttpClient,
    private utils: Utils
  ) {}

  add(trunk: NewTrunk): Observable<TrunkCommon> {
    return this.utils.apiUrl(`/trunks`).pipe(
      mergeMap((url) => this.http.post<TrunkCommon>(url, trunk)),
      wrapError('trunk-repository:add')
    );
  }

  addTeamsTrunk(trunk: NewTrunk): Observable<TrunkCommon> {
    return this.utils.apiUrl(`/teams/trunks`).pipe(
      mergeMap((url) => this.http.post<TrunkCommon>(url, trunk)),
      wrapError('trunk-repository:add-teams-trunk')
    );
  }

  assignUserToTrunk(trunkId: string, loginName: string, force?: boolean): Observable<void> {
    let params = new HttpParams({
      fromObject: {
        force,
        loginName,
      },
    });

    return this.utils.commonApiUrl(`/trunks/${trunkId}/users`).pipe(
      mergeMap((url) =>
        this.http.post<void>(url, undefined, {
          params,
        })
      ),
      wrapError('trunk-repository:assign-user-to-trunk')
    );
  }

  delete(id: Identifier): Observable<void> {
    return this.utils.apiUrl(`/trunks/${id}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('trunk-repository:delete')
    );
  }

  deleteRegistration(trunkId: Identifier, registrationId: string): Observable<void> {
    return this.utils.apiUrl(`/trunks/${trunkId}/registrations/${registrationId}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('trunk-repository:delete-registration')
    );
  }

  deleteTeamsTrunk(id: Identifier): Observable<void> {
    return this.utils.apiUrl(`/teams/trunks/${id}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('trunk-repository:delete-teams-trunk')
    );
  }

  get(id: Identifier): Observable<TrunkCommon> {
    return this.utils.commonApiUrl(`/trunks/${id}`).pipe(
      mergeMap((url) => this.http.get<TrunkCommon>(url)),
      wrapError('trunk-repository:get')
    );
  }

  getFeatures(id: Identifier): Observable<Features> {
    return this.utils.apiUrl(`/trunks/${id}/features`).pipe(
      mergeMap((url) => this.http.get<Features>(url)),
      wrapError('trunk-repository:get-features')
    );
  }

  getTeamsDomain(): Observable<string> {
    return this.utils.apiUrl('/teams/domain').pipe(
      mergeMap((url) => this.http.get<string>(url)),
      wrapError('trunk-repository:get-teams-domain')
    );
  }

  getTeamsDomainSuggestion(): Observable<TeamsDomainSuggestion> {
    return this.utils.apiUrl('/teams/domain/suggestion').pipe(
      mergeMap((url) => this.http.get<TeamsDomainSuggestion>(url)),
      wrapError('trunk-repository:get-teams-domain-suggestion')
    );
  }

  list(options: ListOptions = {}): Observable<TrunkCommon[]> {
    let params = new HttpParams();

    if (options.teams != null) {
      params = params.set('teams', options.teams ? 'true' : 'false');
    }

    if (options.withProfile) {
      params = params.set('withProfile', 'true');
    }

    return this.utils.routeBasedApiUrl(`/trunks`, UserLevel.EnterpriseAdmin).pipe(
      mergeMap((url) =>
        this.http.get<TrunkCommon[]>(url, {
          params,
        })
      ),
      wrapError('trunk-repository:list')
    );
  }

  listProfiles({ includeJson, teams }: ListProfilesOptions): Observable<TrunkProfile[]> {
    let params = new HttpParams();

    if (includeJson) {
      params = params.append('includeJson', 'true');
    }

    if (teams != null) {
      params = params.append('teams', teams ? 'true' : 'false');
    }

    return this.utils.apiUrl(`/trunkProfiles`).pipe(
      mergeMap((url) => this.http.get<TrunkProfile[]>(url, { params })),
      wrapError('trunk-repository:list-profiles')
    );
  }

  listTeamsSBCs(): Observable<TeamsSBC[]> {
    return this.http
      .get<TeamsSBC[]>(`${API_BASE_URL}/reseller/sbcs`)
      .pipe(wrapError('trunk-repository:list-sbcs'));
  }

  readPassword(trunkId: string) {
    return this.utils.apiUrl(`/trunks/${trunkId}/password`).pipe(
      mergeMap((url) => this.http.get<string>(url)),
      wrapError('trunk-repository:read-password')
    );
  }

  removeTrunkAssignment(loginName: string): Observable<void> {
    return this.utils.commonApiUrl(`/users/${loginName}/trunkAssignment`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('trunk-repository:remove-trunk-assignment')
    );
  }

  removeUserFromTrunk(trunkId: string, loginName: string): Observable<void> {
    return this.utils.apiUrl(`/trunks/${trunkId}/users/${loginName}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('trunk-repository:remove-user-from-trunk')
    );
  }

  repairTeamsAssignment(userId: string): Observable<TrunkAssignmentResult> {
    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]: `${BaseUrls.ServiceProvider.Enterprise}/users/${userId}/repairTeamsAssignment`,
        [UserLevel.EnterpriseAdmin]: `${BaseUrls.Enterprise.Enterprise}/users/${userId}/repairTeamsAssignment`,
      })
      .pipe(
        mergeMap((url) => this.http.put<TrunkAssignmentResult>(url, undefined)),
        wrapError('trunk-repository:repair-teams-assignment')
      );
  }

  update(trunkId: string, update: TrunkUpdate): Observable<TrunkCommon> {
    return this.utils.apiUrl(`/trunks/${trunkId}`).pipe(
      mergeMap((url) => this.http.put<TrunkCommon>(url, update)),
      wrapError('trunk-repository:update')
    );
  }

  updateCredentials(trunkId: string, credentials: TrunkCredentials): Observable<TrunkCommon> {
    return this.utils.apiUrl(`/trunks/${trunkId}/credentials`).pipe(
      mergeMap((url) => this.http.put<TrunkCommon>(url, credentials)),
      wrapError('trunk-repository:update-credentials')
    );
  }

  updateFeature(trunkId: string, feature: Feature): Observable<Feature> {
    return this.utils.apiUrl(`/trunks/${trunkId}/features/${feature.id}`).pipe(
      mergeMap((url) => this.http.put<Feature>(url, feature)),
      wrapError('trunk-repository:update-features')
    );
  }

  updateTeamsTrunkData(trunkId: string, update: TeamsTrunkUpdate) {
    return this.utils.apiUrl(`/trunks/${trunkId}/teams`).pipe(
      mergeMap((url) => this.http.put<void>(url, update)),
      wrapError('trunk-repository:update-teams-trunk-data')
    );
  }

  validateTeamsDomain(subdomain: string): Observable<void> {
    return this.utils.apiUrl('/teams/domain/validate').pipe(
      mergeMap((url) =>
        this.http.get<void>(url, {
          params: new HttpParams().set('subdomain', subdomain),
        })
      ),
      wrapError('trunk-repository:validate-teams-domain')
    );
  }
}
